<template>
  <!-- Mobile: action-sheet-style bottom drawer -->
  <UDrawer
    v-if="mobile"
    v-model:open="open"
    :dismissible="true"
    :handle="true"
  >
    <template #content>
      <div class="p-2 pb-[max(var(--app-safe-area-bottom,env(safe-area-inset-bottom)),0.5rem)]">
        <!-- Optional drawer title: on a phone the sheet arrives without its
             trigger in view, so a caption names what the options are. Set as
             a section label (small uppercase, muted) so it reads as the list's
             heading, not as a bolder first option. -->
        <p v-if="title" class="px-4 pt-1.5 pb-1 text-section-label text-muted">{{ title }}</p>

        <!-- No static text color on the row — optionClasses owns it (a
             static one clobbers the destructive red by stylesheet order,
             L27; the desktop branch below is colorless for the same reason). -->
        <button
          v-for="(option, i) in options"
          :key="'opt' + i"
          type="button"
          class="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg text-left font-semibold disabled:opacity-50 active:bg-elevated"
          :class="[optionClasses(option)]"
          :style="option.style"
          :disabled="option.disabled"
          @click="onSelect(option)"
        >
          <i v-if="isFaIcon(option.icon)" class="w-6 text-center text-xl" :class="option.icon" /><!-- type-exception: icon-font glyph -->
          <UIcon v-else-if="option.icon" :name="option.icon" class="size-5 shrink-0" />
          <span class="flex-1">{{ option.text }}</span>
        </button>
      </div>
    </template>
  </UDrawer>

  <!-- Desktop: anchored panel at the trigger element or pointer position
       (clamped to the viewport bottom). pointer-events-auto is load-bearing:
       an open Reka dialog sets pointer-events:none on <body>, which this
       teleported layer would inherit — making the menu click-dead (no
       press-away close, dead options) whenever it opens above a modal.
       Press-away dismisses on pointerdown, never on click/contextmenu: a
       long-press opens the menu while the finger is still down, so the
       backdrop mounts UNDER that finger and the gesture's release lands on
       it (Chromium on Windows sends the touch long-tap contextmenu at
       release, hit-tested there; a tap click would land there too). Only a
       NEW press can produce a pointerdown. The contextmenu stays prevented
       so no native menu appears over ours. The panel is as wide as its
       longest option (w-max, within min-w-44 / max-w-72): it is placed at
       the pointer and measured before being clamped into the viewport, and
       with an auto width a pointer near the right edge would leave it only
       the space there, wrapping its labels. -->
  <Teleport v-else to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 pointer-events-auto"
      @pointerdown.self="close"
      @contextmenu.prevent
    >
      <Transition appear name="cod-menu">
        <div
          ref="panelRef"
          class="absolute w-max min-w-44 max-w-72 overflow-y-auto rounded-card border border-default bg-default py-1"
          :style="panelStyle"
          role="menu"
        >
          <slot name="header" />
          <button
            v-for="(option, i) in options"
            :key="'opt' + i"
            type="button"
            role="menuitem"
            class="w-full flex items-center gap-3 px-3.5 py-2.5 text-left text-body font-semibold disabled:opacity-50 hover:bg-elevated"
            :class="[optionClasses(option)]"
            :style="option.style"
            :disabled="option.disabled"
            @click="onSelect(option)"
          >
            <i v-if="isFaIcon(option.icon)" class="w-5 text-center text-lg" :class="option.icon" /><!-- type-exception: icon-font glyph -->
            <UIcon v-else-if="option.icon" :name="option.icon" class="size-4.5 shrink-0" />
            <span class="flex-1">{{ option.text }}</span>
          </button>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * Context/action menu with a dual presentation: bottom drawer on
 * mobile, anchored panel on desktop (anchored at the opening pointer event —
 * menus open where you click — dismiss-on-select, clamped to the viewport
 * bottom). `options` items
 * carry `{ text, icon, role, disabled, data.action }`; `select` emits
 * `{ action }`. Icons: `fa*` classes render as <i>, anything else as a UIcon
 * (Appendix E names).
 */
import { PropType, computed, nextTick, onBeforeUnmount, ref, watch, type CSSProperties } from 'vue'
import { useDisplay } from '@/composables/useDisplay'

export interface ContextMenuOption {
  text?: string
  icon?: string
  role?: 'destructive' | 'cancel' | string
  disabled?: boolean
  cssClass?: string | string[]
  /** Inline style for the option row (e.g. dynamic per-option colors). */
  style?: CSSProperties
  data?: { action?: string }
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  options: {
    type: Array as PropType<ContextMenuOption[]>,
    default: () => []
  },
  /** Pointer event to anchor at (no auto-open — the host drives v-model). */
  event: {
    type: Object as PropType<PointerEvent | null>,
    default: null
  },
  /** Title of the mobile bottom drawer; the desktop panel opens at the
      pointer next to its trigger and stays title-less. */
  title: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'select'])

const { mobile } = useDisplay()
const panelRef = ref<HTMLElement | null>(null)
const panelStyle = ref<CSSProperties>({})

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
})

function close(): void {
  open.value = false
}

function onSelect(option: ContextMenuOption): void {
  if (option.data?.action) emit('select', { action: option.data.action })
  close() // dismiss-on-select parity
}

function isFaIcon(icon?: string): boolean {
  return !!icon?.startsWith('fa')
}

function optionClasses(option: ContextMenuOption): string[] {
  // The default text color lives HERE, not in the static class list —
  // text-highlighted sits later than text-error in the stylesheet and was
  // silently overriding the destructive red.
  const classes: string[] = [option.role === 'destructive' ? 'text-error' : 'text-highlighted']
  if (option.cssClass) classes.push(...(Array.isArray(option.cssClass) ? option.cssClass : [option.cssClass]))
  return classes
}

/** Anchor point: the opening pointer event (falls back to centered). */
function anchorRect(): { x: number, y: number } | null {
  if (props.event) return { x: props.event.clientX, y: props.event.clientY }
  return null
}

// ESC closes the desktop panel. Window-level (capture) because the panel is a
// plain teleported div — it never holds focus, so its own keydown never fires.
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') { e.stopPropagation(); close() }
}
watch(open, (isOpen) => {
  if (mobile.value) return
  if (isOpen) window.addEventListener('keydown', onKeydown, true)
  else window.removeEventListener('keydown', onKeydown, true)
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown, true))

watch(open, async (isOpen) => {
  if (!isOpen || mobile.value) return
  const anchor = anchorRect()
  if (!anchor) { panelStyle.value = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }; return }

  panelStyle.value = { top: `${anchor.y}px`, left: `${anchor.x}px`, visibility: 'hidden' }
  await nextTick()
  const panel = panelRef.value
  if (!panel) return
  const { innerWidth: vw, innerHeight: vh } = window
  // Layout size, not getBoundingClientRect(): the enter transition still
  // scales the panel to 96% here, so the rect is too small to clamp with.
  const { offsetWidth: width, offsetHeight: height } = panel
  // bottom-fix / right-fix parity: keep the panel inside the viewport.
  const left = Math.min(anchor.x, vw - width - 8)
  const top = Math.min(anchor.y, vh - height - 8)
  panelStyle.value = { top: `${Math.max(8, top)}px`, left: `${Math.max(8, left)}px`, maxHeight: `${vh - 16}px` }
})
</script>

<style scoped>
/* Design §2: 120–160ms ease, no bounce. Scale from the anchor corner. */
.cod-menu-enter-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
  transform-origin: top right;
}
.cod-menu-leave-active {
  transition: opacity 0.1s ease;
}
.cod-menu-enter-from {
  opacity: 0;
  transform: scale(0.96) translateY(-4px);
}
.cod-menu-leave-to {
  opacity: 0;
}
</style>
